// Profile.js
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaUserCircle, FaEnvelope, FaCalendarAlt, FaInfoCircle, FaUser, FaIdCard, FaArrowLeft, FaSignOutAlt, FaTimes, FaExclamationTriangle, FaCheck, FaUserSlash, FaSignInAlt, FaImage, FaUpload } from 'react-icons/fa';
import './Profile.css';

function Profile() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showLogoutModal, setShowLogoutModal] = useState(false);

  // Image upload states
  const [logoFile, setLogoFile] = useState(null);
  const [landingPageFile, setLandingPageFile] = useState(null);
  const [qrCodeFile, setQrCodeFile] = useState(null);
  const [upiId, setUpiId] = useState("");


  const [uploadingImages, setUploadingImages] = useState(false);
  const [uploadMessage, setUploadMessage] = useState(null);
  const [uploadError, setUploadError] = useState(null);

  const navigate = useNavigate();

  useEffect(() => {
    const storedUser = localStorage.getItem('user');

    if (storedUser) {
      try {
        const userData = JSON.parse(storedUser);
        setUser(userData);
        setLoading(false);
      } catch (parseError) {
        console.error('Error parsing user data:', parseError);
        setError('Failed to load profile.');
        setLoading(false);
      }
    } else {
      setError('User not logged in.');
      setLoading(false);
    }
  }, []);

  const handleLogout = () => {
    localStorage.removeItem('user');
    localStorage.removeItem('AUTH_TOKEN_KEY');
    setShowLogoutModal(false);
    navigate('/login');
  };

  const openLogoutModal = () => setShowLogoutModal(true);
  const closeLogoutModal = () => setShowLogoutModal(false);

  const handleUploadImages = async () => {
    if (!logoFile && !landingPageFile && !upiId && !qrCodeFile) {
      setUploadError("Please select at least one image to upload.");
      return;
    }

    setUploadingImages(true);
    setUploadError(null);
    setUploadMessage(null);

    const formData = new FormData();
    if (logoFile) formData.append('logo', logoFile);
    if (landingPageFile) formData.append('landingpage', landingPageFile);
    if (qrCodeFile) formData.append('qrcode', qrCodeFile);
    if (upiId) formData.append('upi_id', upiId);

    try {
      const token = localStorage.getItem('AUTH_TOKEN_KEY');
      const response = await fetch('https://api.meetify.co.in/api/user/upload-images', {
        method: 'POST',
        headers: {
          'x-auth': token,
        },
        body: formData,
      });

      if (!response.ok) {
        throw new Error(`Upload failed with status: ${response.status}`);
      }

      // const result = await response.json();
      setUploadMessage("Images uploaded successfully!");
      setLogoFile(null);
      setLandingPageFile(null);

      // Optionally update user data if API returns new keys
      // if (result && result.user) {
      //   setUser(result.user);
      //   localStorage.setItem('user', JSON.stringify(result.user));
      // }
    } catch (err) {
      console.error("Error uploading images:", err);
      setUploadError("Failed to upload images. Please try again.");
    } finally {
      setUploadingImages(false);
    }
  };

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center min-vh-100 bg-light p-3">
        <div className="text-center">
          <div className="spinner-border text-primary mb-3" style={{ width: '3rem', height: '3rem' }} role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
          <h5 className="text-muted">Loading your profile...</h5>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="d-flex justify-content-center align-items-center min-vh-100 bg-light p-3">
        <div className="alert alert-danger text-center col-lg-6 col-md-8 col-12" role="alert">
          <FaExclamationTriangle className="fs-1 mb-3 text-danger" />
          <h4 className="alert-heading">Oops! Something went wrong</h4>
          <p className="mb-3">{error}</p>
          <button
            className="btn btn-primary"
            onClick={() => navigate('/login')}
          >
            <FaSignInAlt className="me-2" />
            Go to Login
          </button>
        </div>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="d-flex justify-content-center align-items-center min-vh-100 bg-light p-3">
        <div className="alert alert-warning text-center col-lg-6 col-md-8 col-12" role="alert">
          <FaUserSlash className="fs-1 mb-3 text-warning" />
          <h4 className="alert-heading">Profile Not Found</h4>
          <p className="mb-3">Please log in to view your profile details.</p>
          <button
            className="btn btn-primary"
            onClick={() => navigate('/login')}
          >
            <FaSignInAlt className="me-2" />
            Login Now
          </button>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="container-fluid py-4 bg-light min-vh-100">
        <div className="row justify-content-center">
          <div className="col-xxl-8 col-xl-10">
            {/* Profile Header */}
            <div className="row mb-4">
              <div className="col-12">
                <div className="card shadow border-0 rounded-3">
                  <div className="card-header bg-primary text-white py-4 rounded-top-3">
                    <div className="row align-items-center">
                      <div className="col-md-8">
                        <h2 className="h4 mb-0 fw-bold">
                          <FaUserCircle className="me-2" />
                          My Profile
                        </h2>
                        <p className="mb-0 opacity-75">Manage your account information</p>
                      </div>
                      <div className="col-md-4 text-md-end">
                        <span className="badge bg-success fs-6 px-3 py-2">
                          <FaCheck className="me-2" />
                          Active
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Profile Card */}
            <div className="card shadow-lg border-0 rounded-3 profile-card">
              <div className="card-body p-4">
                {/* Profile Image and Basic Info */}
                <div className="row align-items-center mb-4">
                  <div className="col-auto">
                    <div className="position-relative">
                      {user.imageurl ? (
                        <img
                          src={user.imageurl}
                          alt={`${user.name}'s Profile`}
                          className="rounded-circle shadow"
                          style={{ width: '120px', height: '120px', objectFit: 'cover' }}
                        />
                      ) : (
                        <div
                          className="rounded-circle bg-secondary d-flex align-items-center justify-content-center text-white shadow"
                          style={{ width: '120px', height: '120px' }}
                        >
                          <FaUserCircle className="fs-1" />
                        </div>
                      )}
                      <span className="position-absolute bottom-0 end-0 bg-success rounded-circle p-2 border border-3 border-white shadow">
                        <FaCheck className="text-white small" />
                      </span>
                    </div>
                  </div>
                  <div className="col">
                    <h3 className="h5 mb-1 fw-bold text-dark">{user.name}</h3>
                    <div className="mb-2">
                      <FaEnvelope className="me-2 text-muted" />
                      <span className="text-muted">{user.email}</span>
                    </div>
                    <div className="mb-0">
                      <FaCalendarAlt className="me-2 text-muted" />
                      <span className="text-muted small">Member since 2024</span>
                    </div>
                  </div>
                </div>

                {/* Personal Information Section */}
                <div className="row g-3 mb-4">
                  <div className="col-12">
                    <h5 className="fw-bold text-primary mb-3">
                      <FaInfoCircle className="me-2" />
                      Personal Information
                    </h5>
                  </div>

                  <div className="col-lg-6 col-md-12">
                    <div className="card bg-light border-0 h-100">
                      <div className="card-body p-3">
                        <div className="d-flex align-items-center mb-2">
                          <FaUser className="text-primary me-2 fs-5" />
                          <strong className="text-dark">Full Name</strong>
                        </div>
                        <p className="mb-0 text-muted fw-medium">{user.name}</p>
                      </div>
                    </div>
                  </div>

                  <div className="col-lg-6 col-md-12">
                    <div className="card bg-light border-0 h-100">
                      <div className="card-body p-3">
                        <div className="d-flex align-items-center mb-2">
                          <FaEnvelope className="text-primary me-2 fs-5" />
                          <strong className="text-dark">Email Address</strong>
                        </div>
                        <p className="mb-0 text-muted fw-medium">{user.email}</p>
                      </div>
                    </div>
                  </div>

                  <div className="col-12">
                    <div className="card bg-light border-0">
                      <div className="card-body p-3">
                        <div className="d-flex align-items-center mb-2">
                          <FaIdCard className="text-primary me-2 fs-5" />
                          <strong className="text-dark">Account Type</strong>
                        </div>
                        <span className="badge bg-primary fs-6 px-3 py-2">Google Account</span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Branding Images Upload Section */}
                <div className="row g-3 mb-4">
                  <div className="col-12">
                    <h5 className="fw-bold text-primary mb-3">
                      <FaImage className="me-2" />
                      Branding Images
                    </h5>
                  </div>

                  <div className="col-12">
                    <div className="card bg-light border-0">
                      <div className="card-body p-4">
                        {uploadMessage && (
                          <div className="alert alert-success d-flex align-items-center mb-4" role="alert">
                            <FaCheck className="me-2" />
                            <div>{uploadMessage}</div>
                          </div>
                        )}
                        {uploadError && (
                          <div className="alert alert-danger d-flex align-items-center mb-4" role="alert">
                            <FaExclamationTriangle className="me-2" />
                            <div>{uploadError}</div>
                          </div>
                        )}

                        <div className="row g-4">
                          <div className="col-md-6">
                            <label className="form-label fw-bold text-dark">Upload Logo</label>
                            <input
                              type="file"
                              className="form-control"
                              accept="image/*"
                              onChange={(e) => setLogoFile(e.target.files[0])}
                            />
                            <div className="form-text">Choose a logo image (Max 500KB recommended).</div>
                          </div>
                          <div className="col-md-6">
                            <label className="form-label fw-bold text-dark">Upload Landing Page Background</label>
                            <input
                              type="file"
                              className="form-control"
                              accept="image/*"
                              onChange={(e) => setLandingPageFile(e.target.files[0])}
                            />
                            <div className="form-text">Choose a background image for your landing page.</div>
                          </div>
                          <div className="col-md-6">
                            <label className="form-label fw-bold text-dark">Upload Payment QR Code</label>
                            <input
                              type="file"
                              className="form-control"
                              accept="image/*"
                              onChange={(e) => setQrCodeFile(e.target.files[0])}
                            />
                            <div className="form-text">Choose a QR code image for payments.</div>
                          </div>
                          <div className="col-md-6">
                            <label className="form-label fw-bold text-dark">UPI ID</label>
                            <input
                              type="text"
                              className="form-control"
                              value={upiId}
                              onChange={(e) => setUpiId(e.target.value)}
                            />
                            <div className="form-text">Enter your UPI ID for payments.</div>
                          </div>

                        </div>

                        <div className="mt-4 text-end">
                          <button
                            className="btn btn-primary px-4"
                            onClick={handleUploadImages}
                            disabled={uploadingImages || (!logoFile && !landingPageFile && !qrCodeFile && !upiId)}
                          >
                            {uploadingImages ? (
                              <>
                                <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                Uploading...
                              </>
                            ) : (
                              <>
                                <FaUpload className="me-2" />
                                Upload Images
                              </>
                            )}
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Action Buttons */}
                <div className="row">
                  <div className="col-12">
                    <hr className="my-4" />
                    <div className="d-flex flex-column flex-sm-row gap-2 justify-content-sm-end">
                      <button
                        className="btn btn-outline-secondary px-4"
                        onClick={() => window.history.back()}
                        type="button"
                      >
                        <FaArrowLeft className="me-2" />
                        Back
                      </button>
                      <button
                        className="btn btn-danger px-4"
                        onClick={openLogoutModal}
                        type="button"
                      >
                        <FaSignOutAlt className="me-2" />
                        Logout
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Logout Confirmation Modal */}
      {showLogoutModal && (
        <div className="modal fade show d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content border-0 shadow-lg rounded-3">
              <div className="modal-header border-0 pb-0">
                <h5 className="modal-title fw-bold text-danger">
                  <FaExclamationTriangle className="me-2" />
                  Confirm Logout
                </h5>
                <button type="button" className="btn-close" onClick={closeLogoutModal} aria-label="Close"></button>
              </div>
              <div className="modal-body py-4 text-center">
                <FaSignOutAlt className="fs-1 text-danger mb-3 opacity-75" />
                <p className="mb-0 text-muted">
                  Are you sure you want to log out? You'll need to sign in again to access your account.
                </p>
              </div>
              <div className="modal-footer border-0 pt-0">
                <button type="button" className="btn btn-outline-secondary px-4" onClick={closeLogoutModal}>
                  <FaTimes className="me-2" />
                  Cancel
                </button>
                <button type="button" className="btn btn-danger px-4" onClick={handleLogout}>
                  <FaSignOutAlt className="me-2" />
                  Yes, Logout
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export default Profile;